import { Component } from '@theme/component';
import { productContext, trackEvent } from '@theme/analytics-events';

/** Photos above this size, or in formats the artist may not open, are re-encoded as JPEG. */
const REENCODE_THRESHOLD = 12 * 1024 * 1024;
const MAX_DIMENSION = 4096;
const JPEG_QUALITY = 0.92;
const WEB_FRIENDLY_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const IMAGE_EXTENSION_PATTERN = /\.(heic|heif|jpe?g|png|webp|avif)$/i;

const HANDLE_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u;

/** Shade names from the Eye color finder: letters, digits, spaces and simple punctuation. */
const SHADE_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} &'’.-]{0,39}$/u;

/**
 * @typedef {object} BespokeEyeConfiguratorRefs
 * @property {HTMLElement} root - Block wrapper holding the translated messages and limits as data attributes.
 * @property {HTMLInputElement} fileInput - The eye reference photo input.
 * @property {HTMLElement} uploadPanel - Wrapper for the upload controls.
 * @property {HTMLElement} dropzone - The tap/drop area that holds the file input.
 * @property {HTMLElement} preview - Preview shown once a photo is attached.
 * @property {HTMLImageElement} previewImage - Thumbnail of the attached photo.
 * @property {HTMLElement} previewName - File name of the attached photo.
 * @property {HTMLElement} previewFallback - Message shown when the browser cannot preview the format.
 * @property {HTMLElement} photoError - Inline photo validation message.
 * @property {HTMLElement} liveRegion - Single polite live region for configurator status.
 * @property {HTMLInputElement[]} [photoOptions] - "Upload now" / "Send later" radios, when enabled.
 * @property {HTMLInputElement} [engravingInput] - Secret back engraving input, when enabled.
 * @property {HTMLElement} [engravingCount] - Engraving character counter, when enabled.
 * @property {HTMLElement} [recreationNotice] - Shows which sold-out design is being recreated.
 * @property {HTMLImageElement} [recreationImage] - Thumbnail of the design being recreated.
 * @property {HTMLElement} [recreationTitle] - Title of the design being recreated.
 * @property {HTMLInputElement} [recreationInput] - The "Recreation Of" line item property.
 * @property {HTMLElement} [shadeNotice] - Shows the eye color chosen in the Eye color finder.
 * @property {HTMLElement} [shadeTitle] - Name of the chosen eye color.
 * @property {HTMLInputElement} [shadeInput] - The "Eye Color" line item property.
 * @property {HTMLInputElement} [giftInput] - "Gift Order" checkbox, when enabled.
 * @property {HTMLTextAreaElement} [giftMessage] - "Gift Message" for the handwritten card.
 */

/**
 * Bespoke eye configurator: photo upload with preview, photo option toggle, and engraving counter.
 * All fields belong to the buy buttons form through their `form` attribute, so the theme's
 * product form submits them (including the file) as line item properties.
 *
 * @extends Component<BespokeEyeConfiguratorRefs>
 */
class BespokeEyeConfigurator extends Component {
  requiredRefs = ['root', 'fileInput','uploadPanel', 'dropzone', 'preview', 'previewImage', 'previewName', 'liveRegion'];

  /** @type {string | null} */
  #previewUrl = null;

  /** Guards against an older photo finishing processing after a newer pick. */
  #fileGeneration = 0;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.refs.previewImage.addEventListener('error', this.#handlePreviewError, {
      signal: this.#abortController.signal,
    });

    this.#syncPhotoOption();
    this.#loadRecreation(this.#abortController.signal);
    this.#loadShade();
    // Restored form state (back navigation) keeps the message field in step with the gift toggle.
    if (this.refs.giftMessage) this.refs.giftMessage.disabled = !this.refs.giftInput?.checked;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncPhotoOption();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#revokePreview();
  }

  handlePhotoOptionChange() {
    this.#syncPhotoOption();
    const selected = this.refs.photoOptions?.find((option) => option.checked);
    if (selected && selected.value !== this.refs.uploadPanel.dataset.uploadValue) {
      trackEvent('send_photo_later_selected', this.#productContext, { once: true });
    }
  }

  /** "Confirm size later" chosen as the ring size. */
  handleSizeLater() {
    trackEvent('size_later_selected', { ...this.#productContext, source: 'configurator' }, { once: true });
  }

  /** The gift message is only sent while the gift option is on. */
  handleGiftToggle() {
    const { giftInput, giftMessage } = this.refs;
    if (!giftInput || !giftMessage) return;
    giftMessage.disabled = !giftInput.checked;
    if (giftInput.checked) {
      trackEvent('gift_option_selected', this.#productContext, { once: true });
      requestAnimationFrame(() => giftMessage.focus({ preventScroll: true }));
    }
  }

  /** Clears the eye color chosen in the finder and drops it from the URL. */
  removeShade() {
    const { shadeNotice, shadeInput } = this.refs;
    if (!shadeNotice || !shadeInput) return;

    shadeInput.value = '';
    shadeInput.disabled = true;
    shadeNotice.hidden = true;

    const url = new URL(window.location.href);
    url.searchParams.delete('eye_color');
    history.replaceState(history.state, '', url);
  }

  /** Shows ?eye_color=<shade> from the Eye color finder and sends it as the "Eye Color" property. */
  #loadShade() {
    const { shadeNotice, shadeTitle, shadeInput } = this.refs;
    if (!shadeNotice || !shadeTitle || !shadeInput) return;

    const shade = new URL(window.location.href).searchParams.get('eye_color')?.trim();
    if (!shade || !SHADE_PATTERN.test(shade)) return;

    shadeTitle.textContent = shade;
    shadeInput.value = shade;
    shadeInput.disabled = false;
    shadeNotice.hidden = false;
  }

  /** @param {Event} event */
  async handleFileChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const file = input.files?.[0];
    const generation = ++this.#fileGeneration;

    this.#setError('');

    if (!file) {
      this.#showDropzone();
      return;
    }

    if (!this.#isImage(file)) {
      this.#clearInput();
      this.#setError(this.#config.errorNotImage ?? '');
      return;
    }

    this.refs.dropzone.setAttribute('aria-busy', 'true');
    this.#announce(this.#config.messageProcessing ?? '');

    const prepared = await this.#prepareFile(file);

    // A newer pick or a removal happened while this one was processing.
    if (generation !== this.#fileGeneration) return;

    this.refs.dropzone.removeAttribute('aria-busy');

    if (prepared.size > this.#maxFileSize) {
      this.#clearInput();
      this.#setError(this.#config.errorTooLarge ?? '');
      return;
    }

    if (prepared !== file) this.#replaceInputFile(prepared);

    this.#showPreview(prepared);
  }

  replacePhoto() {
    this.refs.fileInput.click();
  }

  removePhoto() {
    this.#fileGeneration++;
    this.#clearInput();
    this.#setError('');
    this.#announce(this.#config.messageRemoved ?? '');
    this.refs.fileInput.focus();
  }

  /** Counts an engraving once it is committed (`change` fires when the field loses focus); never its text. */
  handleEngravingChange() {
    if (!this.refs.engravingInput?.value.trim()) return;
    trackEvent('engraving_entered', this.#productContext, { once: true });
  }

  handleEngravingInput() {
    const { engravingInput, engravingCount } = this.refs;
    const template = this.#config.characterTemplate;
    if (!engravingInput || !engravingCount || !template) return;

    engravingCount.textContent = template
      .replace('[current]', engravingInput.value.length.toString())
      .replace('[max]', engravingInput.maxLength.toString());
  }

  /**
   * Fills the "Recreation Of" property from `?recreation=<handle>`, set by the custom recreation
   * button on sold-out products. The title comes from the storefront, never from the URL itself.
   * @param {AbortSignal} signal
   */
  async #loadRecreation(signal) {
    const { recreationNotice, recreationImage, recreationTitle, recreationInput } = this.refs;
    if (!recreationNotice || !recreationTitle || !recreationInput) return;

    const handle = new URL(window.location.href).searchParams.get('recreation');
    if (!handle || !HANDLE_PATTERN.test(handle)) return;

    const root = (this.#config.productRootUrl ?? '/').replace(/\/$/, '');

    try {
      const response = await fetch(`${root}/products/${encodeURIComponent(handle)}.js`, { signal });
      if (!response.ok) return;

      /** @type {{ title?: string, featured_image?: string | null }} */
      const product = await response.json();
      if (!product.title) return;

      recreationTitle.textContent = product.title;
      recreationInput.value = product.title;
      recreationInput.disabled = false;

      if (recreationImage && product.featured_image) {
        const imageUrl = new URL(product.featured_image, window.location.origin);
        imageUrl.searchParams.set('width', '128');
        recreationImage.src = imageUrl.toString();
        recreationImage.hidden = false;
      }

      recreationNotice.hidden = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      // Without the lookup the order still works; it just lacks the design reference.
    }
  }

  /** The block's data attributes live on the inner wrapper, not on this custom element. */
  get #config() {
    return this.refs.root.dataset;
  }

  /** Product and variant of the buy buttons form these fields belong to. */
  get #productContext() {
    return productContext(this.refs.fileInput.form);
  }

  get #maxFileSize() {
    return Number(this.#config.maxFileSize) || 20 * 1024 * 1024;
  }

  /** Enables the file input only while "Upload now" is the chosen photo option. */
  #syncPhotoOption() {
    const { fileInput, uploadPanel, photoOptions } = this.refs;
    const uploadValue = uploadPanel.dataset.uploadValue;
    const selected = photoOptions?.find((option) => option.checked);
    const uploading = fileInput.hasAttribute('data-always-required') || !selected || selected.value === uploadValue;

    uploadPanel.hidden = !uploading;
    // Disabled inputs are left out of the submitted form data, so no stale photo is sent.
    fileInput.disabled = !uploading;
    fileInput.required = uploading;
    fileInput.setAttribute('aria-required', uploading.toString());
  }

  /** @param {File} file */
  #isImage(file) {
    return file.type.startsWith('image/') || (!file.type && IMAGE_EXTENSION_PATTERN.test(file.name));
  }

  /**
   * Re-encodes very large or non web-friendly photos as a high-quality JPEG when the browser can
   * decode them. Falls back to the original file so a decode failure never blocks the order.
   * @param {File} file
   * @returns {Promise<File>}
   */
  async #prepareFile(file) {
    const needsReencode = file.size > REENCODE_THRESHOLD || !WEB_FRIENDLY_TYPES.includes(file.type);
    if (!needsReencode || typeof createImageBitmap !== 'function') return file;

    /** @type {ImageBitmap | null} */
    let bitmap = null;

    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

      const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);

      const context = canvas.getContext('2d');
      if (!context) return file;

      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      /** @type {Blob | null} */
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
      if (!blob) return file;

      // Keep the original when it is already web friendly and re-encoding would not help.
      if (WEB_FRIENDLY_TYPES.includes(file.type) && blob.size >= file.size) return file;

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'eye-photo';
      return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
    } catch {
      return file;
    } finally {
      bitmap?.close();
    }
  }

  /** @param {File} file */
  #replaceInputFile(file) {
    try {
      const transfer = new DataTransfer();
      transfer.items.add(file);
      this.refs.fileInput.files = transfer.files;
    } catch {
      // Older browsers cannot assign files; the original file stays attached.
    }
  }

  /** @param {File} file */
  #showPreview(file) {
    const { dropzone, preview, previewImage, previewName, previewFallback } = this.refs;

    this.#revokePreview();
    this.#previewUrl = URL.createObjectURL(file);

    previewImage.hidden = false;
    if (previewFallback) previewFallback.hidden = true;
    previewImage.src = this.#previewUrl;
    previewName.textContent = file.name;

    dropzone.hidden = true;
    preview.hidden = false;

    this.#announce((this.#config.messageAttached ?? '').replace('[name]', file.name));
    trackEvent('iris_photo_uploaded', { ...this.#productContext, file_type: file.type || 'unknown' }, { once: true });
  }

  #showDropzone() {
    const { dropzone, preview, previewImage } = this.refs;

    this.#revokePreview();
    previewImage.removeAttribute('src');
    preview.hidden = true;
    dropzone.hidden = false;
    dropzone.removeAttribute('aria-busy');
  }

  #clearInput() {
    this.refs.fileInput.value = '';
    this.#showDropzone();
  }

  #handlePreviewError = () => {
    const { previewImage, previewFallback } = this.refs;
    if (!previewImage.getAttribute('src')) return;

    previewImage.hidden = true;
    if (previewFallback) previewFallback.hidden = false;
  };

  #revokePreview() {
    if (!this.#previewUrl) return;
    URL.revokeObjectURL(this.#previewUrl);
    this.#previewUrl = null;
  }

  /** @param {string} message */
  #setError(message) {
    const { photoError } = this.refs;
    if (!photoError) return;

    photoError.textContent = message;
    photoError.hidden = !message;
    if (message) this.#announce(message);
  }

  /** @param {string} message */
  #announce(message) {
    this.refs.liveRegion.textContent = message;
  }
}

if (!customElements.get('bespoke-eye-configurator-component')) {
  customElements.define('bespoke-eye-configurator-component', BespokeEyeConfigurator);
}
